// src/pages/Gallery.jsx
import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import LoadingSpinner from '../components/LoadingSpinner';

const Gallery = () => {
  const [gallery, setGallery] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [albumModalOpen, setAlbumModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    type: 'image',
    album: 'General',
    url: ''
  });
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterAlbum, setFilterAlbum] = useState('all');
  const [newAlbumName, setNewAlbumName] = useState('');
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));
    };
    updateClock();
    const clockInterval = setInterval(updateClock, 1000);

    const q = query(collection(db, 'gallery'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const galleryData = [];
      snapshot.forEach((doc) => {
        galleryData.push({ id: doc.id, ...doc.data() });
      });
      setGallery(galleryData);
      setLoading(false);
    });

    const albumUnsubscribe = onSnapshot(collection(db, 'albums'), (snapshot) => {
      const albumData = [];
      snapshot.forEach((doc) => {
        albumData.push({ id: doc.id, ...doc.data() });
      });
      setAlbums(albumData);
    });

    return () => {
      clearInterval(clockInterval);
      unsubscribe();
      albumUnsubscribe();
    };
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      type: 'image',
      album: 'General',
      url: ''
    });
    setMediaFile(null);
    setMediaPreview('');
    setEditingItem(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      type: item.type || 'image',
      album: item.album || 'General',
      url: item.url || ''
    });
    if (item.url && item.type === 'image') {
      setMediaPreview(item.url);
    }
    if (item.url && item.type === 'video') {
      setMediaPreview(item.url);
    }
    setModalOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMediaFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setMediaPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsUploading(true);

    try {
      let finalUrl = formData.url;

      if (mediaFile) {
        const storageRef = ref(storage, `gallery/${Date.now()}_${mediaFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, mediaFile);
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => reject(error),
            async () => {
              finalUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      }

      const galleryData = {
        title: formData.title,
        type: formData.type,
        album: formData.album,
        url: finalUrl,
        updatedAt: new Date()
      };

      if (editingItem) {
        if (formData.url && mediaFile && formData.url !== finalUrl) {
          try { await deleteObject(ref(storage, formData.url)); } catch (e) {}
        }
        await updateDoc(doc(db, 'gallery', editingItem.id), galleryData);
        alert('Media updated successfully!');
      } else {
        galleryData.createdAt = new Date();
        await addDoc(collection(db, 'gallery'), galleryData);
        alert('Media uploaded successfully!');
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving media:', error);
      alert('Error saving media: ' + error.message);
    } finally {
      setLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id, url, type) => {
    if (!window.confirm('Are you sure you want to delete this media?')) return;
    
    setLoading(true);
    try {
      if (url && !url.startsWith('http://') && !url.startsWith('https://youtube') && !url.startsWith('https://www.youtube')) {
        try {
          await deleteObject(ref(storage, url));
        } catch (e) {}
      }
      await deleteDoc(doc(db, 'gallery', id));
      alert('Media deleted successfully!');
    } catch (error) {
      console.error('Error deleting media:', error);
      alert('Error deleting media: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAlbum = async (e) => {
    e.preventDefault();
    if (!newAlbumName.trim()) return;
    
    try {
      await addDoc(collection(db, 'albums'), {
        albumName: newAlbumName.trim(),
        createdAt: new Date()
      });
      setNewAlbumName('');
      setAlbumModalOpen(false);
      alert('Album created successfully!');
    } catch (error) {
      console.error('Error adding album:', error);
      alert('Error adding album: ' + error.message);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const closeAlbumModal = () => {
    setAlbumModalOpen(false);
    setNewAlbumName('');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'Invalid date';
    }
  };

  const getFilteredGallery = () => {
    let filtered = [...gallery];
    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.type === filterType);
    }
    if (filterAlbum !== 'all') {
      filtered = filtered.filter(item => item.album === filterAlbum);
    }
    return filtered;
  };

  const styles = `
    .gallery-page {
      animation: structuralSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .management-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 32px;
      background: rgba(255, 255, 255, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.6);
      padding: 16px 24px;
      border-radius: 16px;
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      flex-wrap: wrap;
      gap: 16px;
    }

    .filter-select {
      padding: 10px 20px;
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      outline: none;
      transition: all 0.2s;
    }

    .filter-select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .btn-action-trigger {
      padding: 12px 24px;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.2);
    }

    .btn-action-trigger:hover {
      transform: translateY(-3px);
    }

    .btn-blue {
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
    }

    .btn-blue:hover {
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .btn-purple {
      background: linear-gradient(135deg, #8b5cf6, #6d28d9);
      color: white;
    }

    .btn-purple:hover {
      box-shadow: 0 10px 25px rgba(139, 92, 246, 0.4);
    }

    .gallery-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      gap: 28px;
    }

    .gallery-card-item {
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.6);
      box-shadow: 0 8px 32px 0 rgba(14, 165, 233, 0.06);
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    }

    .gallery-card-item:hover {
      transform: translateY(-6px);
      background: rgba(255, 255, 255, 0.65);
      box-shadow: 0 16px 36px 0 rgba(14, 165, 233, 0.14);
      border-color: rgba(14, 165, 233, 0.3);
    }

    .gallery-image-wrapper {
      position: relative;
      overflow: hidden;
      height: 240px;
      background: linear-gradient(135deg, #0f172a, #334155);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .gallery-actual-img, .gallery-actual-video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.4s ease;
    }

    .gallery-card-item:hover .gallery-actual-img {
      transform: scale(1.06);
    }

    .video-play-hint-overlay {
      position: absolute;
      font-size: 42px;
      color: rgba(255, 255, 255, 0.9);
      text-shadow: 0 4px 15px rgba(0,0,0,0.4);
      pointer-events: none;
      z-index: 2;
    }

    .gallery-card-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(15, 23, 42, 0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      backdrop-filter: blur(4px);
      transition: opacity 0.3s ease;
      z-index: 3;
    }

    .gallery-card-item:hover .gallery-card-overlay {
      opacity: 1;
    }

    .gallery-info-box {
      padding: 20px;
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .gallery-card-title {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.1rem;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 8px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .gallery-badge-row {
      display: flex;
      gap: 6px;
      margin-bottom: 12px;
      flex-wrap: wrap;
    }

    .gallery-card-category {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 6px;
      font-size: 0.72rem;
      font-weight: 700;
      background: rgba(14, 165, 233, 0.12);
      color: #0ea5e9;
      border: 1px solid rgba(14, 165, 233, 0.2);
    }

    .gallery-card-album-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 6px;
      font-size: 0.72rem;
      font-weight: 700;
      background: rgba(139, 92, 246, 0.12);
      color: #8b5cf6;
      border: 1px solid rgba(139, 92, 246, 0.2);
    }

    .gallery-card-meta {
      font-size: 0.78rem;
      color: #64748b;
      font-weight: 600;
      margin-top: auto;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .gallery-card-actions {
      display: flex;
      gap: 10px;
      padding: 16px 20px;
      border-top: 1px solid rgba(14, 165, 233, 0.1);
    }

    .btn-row-action {
      flex: 1;
      padding: 8px 14px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 0.8rem;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: all 0.2s;
      border: none;
    }

    .btn-row-edit {
      background: rgba(14, 165, 233, 0.12);
      border: 1px solid rgba(14, 165, 233, 0.3);
      color: #0ea5e9;
    }

    .btn-row-edit:hover {
      background: #0ea5e9;
      color: white;
    }

    .btn-row-delete {
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      color: #f43f5e;
    }

    .btn-row-delete:hover {
      background: #f43f5e;
      color: white;
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.6);
      grid-column: 1/-1;
    }

    .empty-state i {
      font-size: 54px;
      color: #64748b;
      margin-bottom: 16px;
      display: block;
    }

    .empty-state h3 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.2rem;
      margin-bottom: 6px;
      color: #0f172a;
    }

    .empty-state p {
      color: #64748b;
      font-size: 0.88rem;
      margin-bottom: 20px;
    }

    /* Modal Styles - Positioned at Top */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      min-height: 100vh;
      background: rgba(15, 23, 42, 0.6);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      z-index: 10000;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: 40px 20px;
      overflow-y: auto;
      animation: fadeIn 0.3s ease;
    }

    .modal-viewport-box {
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.6);
      border-radius: 24px;
      padding: 40px;
      width: 100%;
      max-width: 650px;
      max-height: none;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      animation: slideDown 0.3s ease;
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .modal-viewport-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid rgba(14, 165, 233, 0.1);
    }

    .modal-viewport-header h2 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.4rem;
      color: #0f172a;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .modal-viewport-header h2 i {
      color: #0ea5e9;
    }

    .modal-close-btn {
      width: 40px;
      height: 40px;
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      border-radius: 12px;
      color: #f43f5e;
      cursor: pointer;
      font-size: 1.2rem;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-close-btn:hover {
      background: #f43f5e;
      color: white;
      transform: rotate(90deg);
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
    }

    .form-group {
      margin-bottom: 4px;
    }

    .form-group.full-width {
      grid-column: 1 / -1;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
    }

    .form-group label .required {
      color: #f43f5e;
      margin-left: 4px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid rgba(14, 165, 233, 0.15);
      border-radius: 12px;
      font-size: 14px;
      background: rgba(255, 255, 255, 0.6);
      color: #0f172a;
      outline: none;
      transition: all 0.3s ease;
      font-family: inherit;
    }

    .form-group input:focus,
    .form-group textarea:focus,
    .form-group select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .file-upload-wrapper {
      position: relative;
      border: 2px dashed rgba(14, 165, 233, 0.3);
      border-radius: 12px;
      padding: 16px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
      background: rgba(255, 255, 255, 0.3);
    }

    .file-upload-wrapper:hover {
      border-color: #0ea5e9;
      background: rgba(255, 255, 255, 0.5);
    }

    .file-upload-wrapper input[type="file"] {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }

    .file-upload-wrapper .upload-icon {
      font-size: 1.5rem;
      color: #0ea5e9;
      margin-bottom: 4px;
      display: block;
    }

    .file-upload-wrapper .upload-text {
      color: #64748b;
      font-size: 0.8rem;
      font-weight: 600;
    }

    .file-upload-wrapper .upload-text span {
      color: #0ea5e9;
      font-weight: 700;
    }

    .media-preview-wrapper {
      margin-top: 12px;
      position: relative;
      display: inline-block;
      border-radius: 12px;
      overflow: hidden;
      border: 2px solid rgba(14, 165, 233, 0.2);
    }

    .media-preview-wrapper img,
    .media-preview-wrapper video {
      max-width: 150px;
      max-height: 150px;
      display: block;
      border-radius: 8px;
    }

    .media-preview-wrapper .remove-media {
      position: absolute;
      top: 4px;
      right: 4px;
      background: rgba(244, 63, 94, 0.9);
      color: white;
      border: none;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      cursor: pointer;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .media-preview-wrapper .remove-media:hover {
      transform: scale(1.1);
      background: #f43f5e;
    }

    .upload-progress {
      margin-top: 12px;
      background: rgba(14, 165, 233, 0.1);
      border-radius: 8px;
      height: 6px;
      overflow: hidden;
    }

    .upload-progress .progress-bar {
      height: 100%;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      transition: width 0.3s ease;
      border-radius: 8px;
    }

    .modal-footer {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 24px;
      padding-top: 16px;
      border-top: 2px solid rgba(14, 165, 233, 0.1);
    }

    .btn-cancel {
      padding: 10px 24px;
      background: rgba(255, 255, 255, 0.4);
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      color: #334155;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
    }

    .btn-cancel:hover {
      background: #ffffff;
      border-color: #334155;
      transform: translateY(-2px);
    }

    .btn-submit {
      padding: 10px 28px;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .btn-submit:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .btn-submit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-submit .spinner {
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    /* Album Modal */
    .album-modal-box {
      max-width: 450px;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @media (max-width: 992px) {
      .management-toolbar {
        flex-direction: column;
        align-items: stretch;
      }
      .management-toolbar > div {
        justify-content: center;
      }
    }

    @media (max-width: 768px) {
      .gallery-grid {
        grid-template-columns: 1fr;
      }
      .form-grid {
        grid-template-columns: 1fr;
      }
      .modal-viewport-box {
        padding: 24px;
        margin: 10px;
      }
      .modal-viewport-header h2 {
        font-size: 1.2rem;
      }
      .modal-footer {
        flex-direction: column;
      }
      .modal-footer button {
        width: 100%;
        justify-content: center;
      }
    }
  `;

  if (loading && gallery.length === 0) {
    return <LoadingSpinner />;
  }

  const filteredGallery = getFilteredGallery();

  return (
    <>
      <style>{styles}</style>
      <div className="gallery-page">
        <div className="welcome-hero">
          <h1>🖼️ Gallery Management</h1>
          <p>Upload files, map dynamic view albums, and configure live video assets seamlessly.</p>
          <div className="hero-meta-date">
            <i className="far fa-clock"></i> {currentTime}
          </div>
        </div>

        <div className="management-toolbar">
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <select 
              className="filter-select" 
              value={filterType} 
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="image">Images Only</option>
              <option value="video">Videos Only</option>
            </select>
            <select 
              className="filter-select" 
              value={filterAlbum} 
              onChange={(e) => setFilterAlbum(e.target.value)}
            >
              <option value="all">All Albums</option>
              {albums.map(album => (
                <option key={album.id} value={album.albumName}>{album.albumName}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button className="btn-action-trigger btn-purple" onClick={() => setAlbumModalOpen(true)}>
              <i className="fas fa-folder-plus"></i> Create Album
            </button>
            <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
              <i className="fas fa-plus-circle"></i> Upload Media
            </button>
          </div>
        </div>

        <div className="gallery-grid">
          {filteredGallery.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-images"></i>
              <h3>No Media Assets Located</h3>
              <p>The configuration criteria map isolated zero matching data entries.</p>
              <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
                <i className="fas fa-plus-circle"></i> Upload Your First Media
              </button>
            </div>
          ) : (
            filteredGallery.map((item) => {
              const isVideo = item.type === 'video';
              return (
                <div className="gallery-card-item" key={item.id}>
                  <div className="gallery-image-wrapper">
                    {isVideo ? (
                      <>
                        <video src={item.url} className="gallery-actual-video" muted preload="metadata" />
                        <div className="video-play-hint-overlay">
                          <i className="fas fa-circle-play"></i>
                        </div>
                      </>
                    ) : (
                      <img src={item.url} alt={item.title} className="gallery-actual-img" />
                    )}
                    <div className="gallery-card-overlay">
                      <button 
                        className="btn-row-action btn-row-edit" 
                        style={{ background: '#ffffff', color: '#0f172a', maxWidth: '110px' }}
                        onClick={() => window.open(item.url, '_blank')}
                      >
                        <i className={`fas ${isVideo ? 'fa-play' : 'fa-maximize'}`}></i> Preview
                      </button>
                    </div>
                  </div>
                  <div className="gallery-info-box">
                    <div className="gallery-badge-row">
                      <span className="gallery-card-category" style={{
                        background: isVideo ? 'rgba(16,185,129,0.12)' : 'rgba(14,165,233,0.12)',
                        color: isVideo ? '#10b981' : '#0ea5e9'
                      }}>
                        {isVideo ? 'Video Type' : 'Image Type'}
                      </span>
                      <span className="gallery-card-album-badge">{item.album || 'No Album Assigned'}</span>
                    </div>
                    <h3 className="gallery-card-title">{item.title || 'Untitled Asset'}</h3>
                    <div className="gallery-card-meta">
                      <i className="far fa-calendar"></i>
                      <span>Uploaded: {formatDate(item.createdAt)}</span>
                    </div>
                  </div>
                  <div className="gallery-card-actions">
                    <button className="btn-row-action btn-row-edit" onClick={() => openEditModal(item)}>
                      <i className="fas fa-pen-to-square"></i> Edit
                    </button>
                    <button className="btn-row-action btn-row-delete" onClick={() => handleDelete(item.id, item.url, item.type)}>
                      <i className="fas fa-trash-can"></i> Delete
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Gallery Modal - Positioned at Top */}
        {modalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-viewport-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-viewport-header">
                <h2>
                  <i className={`fas ${editingItem ? 'fa-edit' : 'fa-plus-circle'}`}></i>
                  {editingItem ? 'Edit Media Asset' : 'Upload New Media'}
                </h2>
                <button className="modal-close-btn" onClick={closeModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group full-width">
                  <label>Asset Title <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Campus Laboratory Grid Capture"
                    required
                  />
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>Media Type <span className="required">*</span></label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      required
                    >
                      <option value="image">Image File</option>
                      <option value="video">Video Stream</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Assign Album <span className="required">*</span></label>
                    <select
                      value={formData.album}
                      onChange={(e) => setFormData({ ...formData, album: e.target.value })}
                      required
                    >
                      <option value="General">General Album</option>
                      {albums.map(album => (
                        <option key={album.id} value={album.albumName}>{album.albumName}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {formData.type === 'video' && (
                  <div className="form-group full-width">
                    <label>Or Paste Video Link (YouTube / Direct Streaming MP4 Url)</label>
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                )}

                <div className="form-group full-width">
                  <label>{formData.type === 'video' ? 'Upload Video File' : 'Upload Image File'}</label>
                  <div className="file-upload-wrapper">
                    <i className={`fas ${formData.type === 'video' ? 'fa-video' : 'fa-image'} upload-icon`}></i>
                    <div className="upload-text">
                      Drag & drop or <span>browse</span> to upload {formData.type === 'video' ? 'video' : 'image'}
                    </div>
                    <input
                      type="file"
                      accept={formData.type === 'video' ? 'video/*' : 'image/*'}
                      onChange={handleMediaChange}
                    />
                  </div>
                  {mediaPreview && (
                    <div className="media-preview-wrapper">
                      {formData.type === 'video' ? (
                        <video src={mediaPreview} muted style={{ maxWidth: '150px', maxHeight: '150px' }} />
                      ) : (
                        <img src={mediaPreview} alt="Preview" />
                      )}
                      <button
                        type="button"
                        className="remove-media"
                        onClick={() => {
                          setMediaPreview('');
                          setMediaFile(null);
                          setFormData({ ...formData, url: '' });
                        }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>

                {isUploading && uploadProgress > 0 && (
                  <div className="upload-progress">
                    <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                )}

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeModal}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={isUploading}>
                    {isUploading ? (
                      <>
                        <span className="spinner"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        {editingItem ? 'Update Asset' : 'Upload Asset'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Album Modal - Positioned at Top */}
        {albumModalOpen && (
          <div className="modal-overlay" onClick={closeAlbumModal}>
            <div className="modal-viewport-box album-modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-viewport-header">
                <h2>
                  <i className="fas fa-folder-plus" style={{ color: '#8b5cf6' }}></i>
                  Create New Album
                </h2>
                <button className="modal-close-btn" onClick={closeAlbumModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleAddAlbum}>
                <div className="form-group">
                  <label>Album Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={newAlbumName}
                    onChange={(e) => setNewAlbumName(e.target.value)}
                    placeholder="e.g., Convocation 2026"
                    required
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeAlbumModal}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                  <button type="submit" className="btn-submit" style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)' }}>
                    <i className="fas fa-save"></i> Save Album
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Gallery;